const NUMERIC_VALUE_TYPES = new Set([
  "NUMBER",
  "INTEGER",
  "INTEGER_POSITIVE",
  "INTEGER_NEGATIVE",
  "INTEGER_ZERO_OR_POSITIVE",
  "PERCENTAGE",
  "AGE",
]);
const BOOLEAN_VALUE_TYPES = new Set(["BOOLEAN", "TRUE_ONLY"]);
const COORDINATE_VALUE_TYPES = new Set(["COORDINATE"]);
const TRACKER_GEOMETRY_FIELD_NAME = "__trackerGeometry";
const GHANA_LOCAL_PHONE_PATTERN = /^0\d{9}$/;
const GHANA_COUNTRY_PHONE_PATTERN = /^\+?233\d{9}$/;

function formatDatePart(value, length = 2) {
  return String(value).padStart(length, "0");
}

export function formatTrackerRegistrationDateTime(date) {
  return [
    `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}-${formatDatePart(date.getDate())}`,
    `${formatDatePart(date.getHours())}:${formatDatePart(date.getMinutes())}:${formatDatePart(
      date.getSeconds(),
    )}.${formatDatePart(date.getMilliseconds(), 3)}`,
  ].join("T");
}

function generateDhis2Uid() {
  const letters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const characters = `${letters}0123456789`;
  const firstCharacter = letters[Math.floor(Math.random() * letters.length)];
  const remainingCharacters = Array.from(
    { length: 10 },
    () => characters[Math.floor(Math.random() * characters.length)],
  ).join("");

  return `${firstCharacter}${remainingCharacters}`;
}

function normalizeDateValue(value) {
  const stringValue = String(value || "").trim();

  if (!stringValue) {
    return undefined;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return stringValue;
  }

  const parsedDate = new Date(stringValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return stringValue;
  }

  return `${parsedDate.getFullYear()}-${formatDatePart(parsedDate.getMonth() + 1)}-${formatDatePart(
    parsedDate.getDate(),
  )}`;
}

function normalizeDateTimeValue(value) {
  const stringValue = String(value || "").trim();

  if (!stringValue) {
    return undefined;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(stringValue)) {
    return `${stringValue}:00.000`;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(stringValue)) {
    return `${stringValue}.000`;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/.test(stringValue)) {
    return stringValue;
  }

  const parsedDate = new Date(stringValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return stringValue;
  }

  return formatTrackerRegistrationDateTime(parsedDate);
}

function normalizeBooleanValue(value) {
  if (typeof value === "boolean") {
    return value;
  }

  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "true" || normalized === "yes") {
    return true;
  }

  if (normalized === "false" || normalized === "no") {
    return false;
  }

  return undefined;
}

function normalizeNumericValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const normalized = String(value || "").trim();

  if (!normalized) {
    return undefined;
  }

  const parsedNumber = Number(normalized);
  return Number.isFinite(parsedNumber) ? parsedNumber : normalized;
}

function normalizeCoordinateValue(value) {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized || undefined;
  }

  if (typeof value === "object") {
    const latitude = String(value?.latitude || "").trim();
    const longitude = String(value?.longitude || "").trim();

    if (!latitude && !longitude) {
      return undefined;
    }

    if (!latitude || !longitude) {
      return undefined;
    }

    return `[${longitude},${latitude}]`;
  }

  return undefined;
}

function isPhoneNumberField(field) {
  const valueType = String(field?.valueType || "").toUpperCase();
  const fieldText = [
    field?.label,
    field?.name,
    field?.description,
    field?.displayName,
  ].join(" ");

  return valueType === "PHONE_NUMBER" || /\b(phone|telephone|mobile|contact\s+number)\b/i.test(fieldText);
}

function normalizePhoneNumberForValidation(value) {
  return String(value || "")
    .trim()
    .replace(/[\s().-]+/g, "");
}

export function isValidGhanaPhoneNumber(value) {
  const normalizedValue = normalizePhoneNumberForValidation(value);

  return (
    GHANA_LOCAL_PHONE_PATTERN.test(normalizedValue) ||
    GHANA_COUNTRY_PHONE_PATTERN.test(normalizedValue)
  );
}

export function getPhoneNumberValidationMessage(field, value) {
  if (!isPhoneNumberField(field)) {
    return "";
  }

  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }

  return isValidGhanaPhoneNumber(rawValue)
    ? ""
    : `${field?.label || "Phone number"} must be a valid 10-digit Ghana number, for example 0236578990 or +233236578990.`;
}

function normalizeTrackerRegistrationValue(rawValue, field) {
  if (rawValue === null || rawValue === undefined) {
    return undefined;
  }

  if (typeof rawValue === "string" && rawValue.trim() === "") {
    return undefined;
  }

  const valueType = String(field?.valueType || "").toUpperCase();

  if (BOOLEAN_VALUE_TYPES.has(valueType)) {
    return normalizeBooleanValue(rawValue);
  }

  if (COORDINATE_VALUE_TYPES.has(valueType) || field?.type === "coordinate") {
    return normalizeCoordinateValue(rawValue);
  }

  if (NUMERIC_VALUE_TYPES.has(valueType)) {
    return normalizeNumericValue(rawValue);
  }

  if (valueType === "DATE") {
    return normalizeDateValue(rawValue);
  }

  if (valueType === "DATETIME") {
    return normalizeDateTimeValue(rawValue);
  }

  return typeof rawValue === "string" ? rawValue.trim() : rawValue;
}

function flattenRegistrationFields(sections) {
  return (sections || []).flatMap((section) => section?.fields || []);
}

function getGeneratedRegistrationFieldIds(sections) {
  return new Set(
    flattenRegistrationFields(sections)
      .filter((field) => field?.generated && field?.name)
      .map((field) => field.name),
  );
}

function removeGeneratedAttributes(attributes, generatedFieldIds) {
  if (!Array.isArray(attributes) || generatedFieldIds.size === 0) {
    return attributes;
  }

  return attributes.filter((attribute) => !generatedFieldIds.has(attribute?.attribute));
}

function stripGeneratedRegistrationAttributes(payload, sections) {
  const generatedFieldIds = getGeneratedRegistrationFieldIds(sections);
  if (generatedFieldIds.size === 0) {
    return payload;
  }

  return {
    ...payload,
    trackedEntities: (payload?.trackedEntities || []).map((trackedEntity) => ({
      ...trackedEntity,
      attributes: removeGeneratedAttributes(trackedEntity?.attributes, generatedFieldIds),
      enrollments: Array.isArray(trackedEntity?.enrollments)
        ? trackedEntity.enrollments.map((enrollment) => ({
            ...enrollment,
            attributes: removeGeneratedAttributes(enrollment?.attributes, generatedFieldIds),
          }))
        : trackedEntity?.enrollments,
    })),
  };
}

function buildPointGeometry(value) {
  const normalizedValue = normalizeCoordinateValue(value);

  if (!normalizedValue) {
    return null;
  }

  const match = String(normalizedValue).match(/^\[\s*([^,\]]+)\s*,\s*([^,\]]+)\s*\]$/);
  if (!match) {
    return null;
  }

  const longitude = Number(match[1]);
  const latitude = Number(match[2]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    type: "Point",
    coordinates: [longitude, latitude],
  };
}

export function buildTrackerRegistrationGeometry(formData, sections) {
  const geometryField = flattenRegistrationFields(sections).find(
    (field) => field?.isGeometry || field?.name === TRACKER_GEOMETRY_FIELD_NAME,
  );

  if (!geometryField) {
    return null;
  }

  return buildPointGeometry(formData?.[geometryField.name]);
}

export function buildTrackerRegistrationAttributes(formData, sections) {
  return flattenRegistrationFields(sections).reduce((attributes, field) => {
    if (!field?.name) {
      return attributes;
    }

    if (field?.generated) {
      return attributes;
    }

    if (field?.isGeometry || field?.name === TRACKER_GEOMETRY_FIELD_NAME) {
      return attributes;
    }

    const value = normalizeTrackerRegistrationValue(formData?.[field.name], field);

    if (value === undefined) {
      return attributes;
    }

    attributes.push({
      attribute: field.name,
      value,
    });

    return attributes;
  }, []);
}

/*
 * Handoff note:
 * - Phase 1 only submits backend-driven tracker forms because those field names already
 *   match DHIS2 tracked entity attribute ids one-to-one.
 * - `orgUnitId` comes from the current location scope, while `programId` and
 *   `trackedEntityTypeId` come from the selected tracker metadata.
 * - We stamp one submission timestamp and reuse it for both `enrolledAt` and
 *   `occurredAt` so the backend receives a single, predictable submission moment.
 */
export function buildTrackerRegistrationPayload({
  formData,
  orgUnitId,
  programId,
  trackedEntityTypeId,
  sections,
  trackedEntityId = null,
  submittedAt = new Date(),
}) {
  if (!orgUnitId) {
    throw new Error("Choose a district or sub-area before registering a tracker record.");
  }

  if (!programId || !trackedEntityTypeId) {
    throw new Error("Tracker details are still loading. Refresh and try again.");
  }

  const attributes = buildTrackerRegistrationAttributes(formData, sections);
  const geometry = buildTrackerRegistrationGeometry(formData, sections);

  if (attributes.length === 0 && !geometry) {
    throw new Error("Enter at least one tracker field before submitting.");
  }

  const submissionDateTime = formatTrackerRegistrationDateTime(submittedAt);
  const resolvedTrackedEntityId = trackedEntityId || generateDhis2Uid();

  const trackedEntity = trackedEntityId
    ? {
        trackedEntity: resolvedTrackedEntityId,
        orgUnit: orgUnitId,
        trackedEntityType: trackedEntityTypeId,
        attributes,
        ...(geometry ? { geometry } : {}),
      }
    : {
        trackedEntity: resolvedTrackedEntityId,
        orgUnit: orgUnitId,
        trackedEntityType: trackedEntityTypeId,
        attributes,
        ...(geometry ? { geometry } : {}),
        enrollments: [
          {
            program: programId,
            orgUnit: orgUnitId,
            enrolledAt: submissionDateTime,
            occurredAt: submissionDateTime,
            status: "ACTIVE",
            attributes: attributes.map((attribute) => ({ ...attribute })),
            ...(geometry ? { geometry } : {}),
          },
        ],
      };

  return stripGeneratedRegistrationAttributes({ trackedEntities: [trackedEntity] }, sections);
}
